import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import SettingSection from "../../shared/SettingSection"
import { Loader, MediumText } from "@/UI"
import EmptySettingBox from "../../shared/EmptySettingBox.container"
import { useRecoilValue } from "recoil"
import { sortedDispatchRulesAtom } from "@/atoms/dispatchRulesAtom"
import { isEmpty } from "lodash"
import { useDispatchRule } from "@/hooks/useDispatchRule"
import DispatchRulesList from "./DispatchRulesList/DispatchRulesList.container"
import { userAtom } from "@/atoms/userAtom"
import Image from "next/image"
import RuleFormModalButton from "./RuleFormModalButton/RuleFormModalButton.container"
import SettingContentWrapper from "../../shared/SettingContentWrapper"
import Reassign from "./Reassign"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Autoassign from "./Autoassign"
import { Tabs } from "antd"
import PreparationSettings from "./Preparation"

interface DispatchSettingsProps {}

const DispatchSettings: React.FC<DispatchSettingsProps> = () => {
  const { t } = useTranslation()
  const user = useRecoilValue(userAtom)
  const dispatchRules = useRecoilValue(sortedDispatchRulesAtom)
  const { fetchDispatchRules, isLoading } = useDispatchRule()

  useEffect(() => {
    fetchDispatchRules()
  }, [user?.org])

  const tabs = [
    {
      label: t("common.settings"),
      key: "settings",
      children: (
        <>
          <SettingContentWrapper>
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between">
                <MediumText>{t("profile.DispatchSettings.autoassign")}</MediumText>
              </div>
              <Autoassign />
            </div>
          </SettingContentWrapper>
          <SettingContentWrapper>
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between">
                <MediumText>{t("profile.DispatchSettings.preparationSettings")}</MediumText>
              </div>
              <PreparationSettings />
            </div>
          </SettingContentWrapper>
          <SettingContentWrapper>
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between">
                <MediumText>{t("profile.DispatchSettings.reassign")}</MediumText>
              </div>
              <Reassign />
            </div>
          </SettingContentWrapper>
        </>
      ),
    },
    {
      label: t("profile.DispatchSettings.dispatchRules"),
      key: "rules",
      children: (
        <>
          {isLoading ? (
            <Loader fontSize={48} />
          ) : (
            <>
              {isEmpty(dispatchRules) ? (
                <EmptySettingBox
                  icon={
                    <Image
                      src={"/assets/images/profile/dispatchRulesPlaceholder.svg"}
                      alt="logo"
                      width={80}
                      height={80}
                    />
                  }
                  description={t("profile.EmptySettingBox.startCreatingRules")}
                  button={
                    <RuleFormModalButton
                      button={
                        <KosmoButton id="create-new-rule-button" type="primary">
                          <div className="flex items-center gap-x-2">
                            <FontAwesomeIcon icon={faPlus} color="#FFF" />
                            <p>{t("profile.EmptySettingBox.createNewRule")}</p>
                          </div>
                        </KosmoButton>
                      }
                    />
                  }
                />
              ) : (
                <DispatchRulesList dispatchRules={dispatchRules} />
              )}
            </>
          )}
        </>
      ),
    },
  ]

  return (
    <SettingSection title={t("profile.DispatchSettings.dispatchSettings")}>
      <Tabs items={tabs} className="w-full" />
    </SettingSection>
  )
}

export default DispatchSettings
